import {BaseOfflineDb} from '@app/offline/base-offline-db';
import {Track} from '@app/web-player/tracks/track';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {extensionFromFilename} from '@ui/utils/files/extension-from-filename';

export type DownloadedFile = {
  size: number;
  contentType: string;
  extension: string;
  id: number;
  name: string;
  lastModified: number;
  // optional cover art stored inline so it can be rendered offline without
  // touching the network — populated alongside the audio when available
  imageBlob?: Blob;
  imageContentType?: string;
};

type DownloadProgress = {
  loaded: number;
  total: number | null;
  percentage: number | null;
};

export type ProgressCallback = (progress: DownloadProgress) => void;

class _DownloadedFiles extends BaseOfflineDb<DownloadedFile> {
  protected dbName = 'downloaded_files';
  protected dbVersion = 1;
  protected dbStoreName = 'files';

  async add(file: DownloadedFile): Promise<DownloadedFile | null> {
    if (await this.putItem(file)) {
      return file;
    }
    return null;
  }

  async getByTrackId(trackId: string | number): Promise<DownloadedFile | null> {
    return this.getItem(+trackId);
  }

  async delete(downloadedFile: DownloadedFile): Promise<boolean> {
    return this.deleteItem(downloadedFile.id);
  }

  async clear(): Promise<boolean> {
    return this.deleteAllItems();
  }
}

export const downloadedFiles = new _DownloadedFiles();

export async function downloadAndStoreTrackPlaybackData(
  track: Track,
  abortSignal: AbortSignal,
  onProgress: ProgressCallback,
): Promise<boolean> {
  const storageDir = await getStorageRoot();

  if (await playbackDataAlreadyExists(track.id)) {
    return true;
  }

  // download the playback data from backend
  const {blob, contentType} = await downloadData(
    track.id,
    abortSignal,
    onProgress,
  );

  const filename = `${track.id}`;

  // store downloaded data in opfs
  const fileHandle = await storageDir.getFileHandle(filename, {create: true});
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();

  // best-effort: also grab the cover art so the song renders correctly offline
  // (especially on iOS WKWebView wrappers where the SW image cache is absent)
  const image = await fetchTrackImage(track, abortSignal);

  // create a new entry in indexeddb for this download
  const entry = await downloadedFiles.add({
    id: +track.id,
    name: filename,
    size: blob.size,
    contentType: contentType,
    extension: guessExtension(track, contentType),
    lastModified: Date.now(),
    imageBlob: image?.blob,
    imageContentType: image?.contentType,
  });

  return !!entry;
}

async function fetchTrackImage(
  track: Track,
  abortSignal: AbortSignal,
): Promise<{blob: Blob; contentType: string} | null> {
  if (!track.image) return null;
  try {
    const response = await fetch(track.image, {signal: abortSignal});
    if (!response.ok) return null;
    const blob = await response.blob();
    const contentType =
      response.headers.get('content-type') || blob.type || 'image/jpeg';
    return {blob, contentType};
  } catch {
    return null;
  }
}

function guessExtension(track: Track, contentType: string): string {
  const fromFileName = extensionFromFilename(track.name);
  if (fromFileName) return fromFileName;

  // Strip parameters like "; codecs=opus"
  const mimeType = contentType.split(';')[0].trim().toLowerCase();
  const fromMap = mimeToExtensionMap[mimeType];
  if (fromMap) return fromMap;

  // Fallback: use the subtype part of the MIME type
  const subtype = mimeType.split('/')[1];
  if (subtype) return subtype;

  return 'mp3';
}

async function downloadData(
  trackId: string | number,
  abortSignal: AbortSignal,
  onProgress: ProgressCallback,
) {
  // ?stream=1 forces the backend to stream the file through PHP rather than
  // redirecting to a presigned S3 URL. Without it the fetch() below follows
  // the redirect into S3 and is rejected by CORS, so the OPFS write never
  // happens and the track silently never becomes available offline.
  const url = `${getBootstrapData().settings.base_url}/api/v1/tracks/${trackId}/download?stream=1`;

  const response = await fetch(url, {signal: abortSignal});

  if (!response.ok || !response.body) {
    throw new Error(
      `Failed to download track playback data: ${response.status} ${response.statusText}`,
    );
  }

  const contentLength = response.headers.get('content-length');
  const contentType =
    response.headers.get('content-type') || 'application/octet-stream';
  // Content-Length is absent when the backend streams the response with
  // Transfer-Encoding: chunked (the ?stream=1 path). That's fine — we just
  // can't report a percentage, but the file still downloads correctly.
  const total = contentLength ? parseInt(contentLength, 10) : null;

  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];
  let loaded = 0;

  while (true) {
    const {done, value} = await reader.read();

    if (done) {
      break;
    }

    if (value) {
      chunks.push(value);
      loaded += value.length;

      if (onProgress) {
        const percentage = total !== null ? (loaded / total) * 100 : null;
        onProgress({
          loaded,
          total,
          percentage,
        });
      }
    }
  }

  return {
    blob: new Blob(chunks),
    contentType: contentType,
  };
}

async function playbackDataAlreadyExists(
  trackId: string | number,
): Promise<boolean> {
  const entry = await downloadedFiles.getByTrackId(trackId);
  if (!entry) return false;

  try {
    const storageDir = await getStorageRoot();
    await storageDir.getFileHandle(entry.name, {create: false});
    return true;
  } catch (e: any) {
    if (e.name === 'NotFoundError') {
      return false;
    }

    throw e;
  }
}

const OFFLINE_PLAYBACK_DATA_DIR = 'offline-playback-data';

async function getStorageRoot(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return await root.getDirectoryHandle(OFFLINE_PLAYBACK_DATA_DIR, {
    create: true,
  });
}

export async function deleteTrackPlaybackData(
  trackId: string | number,
): Promise<boolean> {
  const entry = await downloadedFiles.getByTrackId(trackId);
  if (!entry) return true;

  try {
    await downloadedFiles.delete(entry);
    const storageDir = await getStorageRoot();
    await storageDir.removeEntry(entry.name);
    revokeOfflinedTrackBlobUrl(+trackId);
    revokeOfflinedTrackImageBlobUrl(+trackId);
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'NotFoundError') {
      return true;
    }

    throw e;
  }
}

export async function clearPlaybackData(): Promise<boolean> {
  const handle = await navigator.storage.getDirectory();
  downloadedFiles.clear();
  try {
    await handle.removeEntry(OFFLINE_PLAYBACK_DATA_DIR, {recursive: true});
  } catch (e) {
    const notFound = e instanceof DOMException && e.name === 'NotFoundError';
    if (!notFound) {
      throw e;
    }
  }
  revokeAllOfflinedTrackBlobUrls();
  revokeAllOfflinedTrackImageBlobUrls();

  return true;
}

// The player persists its queue (MediaItems with their `src`) to localStorage
// with this key (see `<PlayerContext id="web-player">`). On a cold offline
// boot the queue is restored verbatim, so any track originally queued with a
// remote streaming URL will keep trying to fetch from the network. This
// helper rewrites those entries to point at the offlined OPFS/blob source,
// so previously-queued downloaded tracks play offline without forcing the
// user to rebuild the queue from the UI.
const PERSISTED_QUEUE_LS_KEY = 'player.web-player.queue';

export async function rehydratePersistedQueueWithOfflineSources(): Promise<void> {
  let queue: any[];
  try {
    const raw = localStorage.getItem(PERSISTED_QUEUE_LS_KEY);
    if (!raw) return;
    queue = JSON.parse(raw);
    if (!Array.isArray(queue) || queue.length === 0) return;
  } catch {
    return;
  }

  let changed = false;
  await Promise.all(
    queue.map(async item => {
      if (item?.provider !== 'htmlAudio' || item?.meta?.id == null) return;
      const currentSrc: string = item.src || '';
      if (
        currentSrc.includes('stream/offlined-media/') ||
        currentSrc.startsWith('blob:')
      ) {
        return; // already pointing at an offline source
      }
      try {
        const entry = await downloadedFiles.getByTrackId(item.meta.id);
        if (!entry) return;
        let src = `stream/offlined-media/${item.meta.id}.${entry.extension}`;
        if (!navigator.serviceWorker?.controller) {
          const blob = await getOfflinedTrackBlobUrl(item.meta.id);
          if (blob) src = blob;
        }
        if (item.src !== src) {
          item.src = src;
          changed = true;
        }
      } catch {
        // best-effort: leave the entry untouched if anything fails
      }
    }),
  );

  if (changed) {
    try {
      localStorage.setItem(PERSISTED_QUEUE_LS_KEY, JSON.stringify(queue));
    } catch {
      // ignore quota / serialisation errors
    }
  }
}

// Used as a fallback to the `/stream/offlined-media/*` service-worker route
// in environments where the SW cannot run (e.g. iOS WKWebView inside a
// PWABuilder wrapper). We read the downloaded file directly from OPFS and
// expose it as a `blob:` URL the <audio> element can play.
const offlineBlobUrlCache = new Map<number, string>();

export async function getOfflinedTrackBlobUrl(
  trackId: number,
): Promise<string | null> {
  const cached = offlineBlobUrlCache.get(trackId);
  if (cached) return cached;

  const entry = await downloadedFiles.getByTrackId(trackId);
  if (!entry) return null;

  try {
    const storageDir = await getStorageRoot();
    const fileHandle = await storageDir.getFileHandle(entry.name);
    const file = await fileHandle.getFile();
    const url = URL.createObjectURL(file);
    offlineBlobUrlCache.set(trackId, url);
    return url;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'NotFoundError') {
      return null;
    }
    throw e;
  }
}

export function revokeOfflinedTrackBlobUrl(trackId: number): void {
  const url = offlineBlobUrlCache.get(trackId);
  if (url) {
    URL.revokeObjectURL(url);
    offlineBlobUrlCache.delete(trackId);
  }
}

export function revokeAllOfflinedTrackBlobUrls(): void {
  for (const url of offlineBlobUrlCache.values()) {
    URL.revokeObjectURL(url);
  }
  offlineBlobUrlCache.clear();
}

// Same pattern as the audio cache, but for the cover image stored alongside
// each downloaded track. Used by <TrackImage> when an offlined track is
// rendered.
const offlineImageUrlCache = new Map<number, string>();

export async function getOfflinedTrackImageBlobUrl(
  trackId: number,
): Promise<string | null> {
  const cached = offlineImageUrlCache.get(trackId);
  if (cached) return cached;

  const entry = await downloadedFiles.getByTrackId(trackId);
  if (!entry?.imageBlob) return null;

  const url = URL.createObjectURL(entry.imageBlob);
  offlineImageUrlCache.set(trackId, url);
  return url;
}

export function revokeOfflinedTrackImageBlobUrl(trackId: number): void {
  const url = offlineImageUrlCache.get(trackId);
  if (url) {
    URL.revokeObjectURL(url);
    offlineImageUrlCache.delete(trackId);
  }
}

export function revokeAllOfflinedTrackImageBlobUrls(): void {
  for (const url of offlineImageUrlCache.values()) {
    URL.revokeObjectURL(url);
  }
  offlineImageUrlCache.clear();
}

// only partial map for mimes that are plyable by the browser
const mimeToExtensionMap: Record<string, string> = {
  // Audio
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'aac',
  'audio/aacp': 'aac',
  'audio/ogg': 'ogg',
  'audio/webm': 'webm',
  'audio/wav': 'wav',
  'audio/wave': 'wav',
  'audio/x-wav': 'wav',
  'audio/flac': 'flac',
  'audio/x-flac': 'flac',

  // Video
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/ogg': 'ogv',
};
